import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Chip,
  IconButton,
  TextField,
  Stack,
  MenuItem,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import {
  Delete,
  Add,
  ConfirmationNumber
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { couponService } from '../services/couponService';
import { Coupon, Campaign, Store } from '../types';
import { campaignService } from '../services/campaignService';
import { storeService } from '../services/storeService';
import { AppHeader } from '../components/AppHeader';

  const CouponList: React.FC = () => {
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [filters, setFilters] = useState({
    number: '',
    campaignId: '',
    storeId: '',
    sellerName: '',
    userName: '',
    status: ''
  });

  const fetchCoupons = useCallback(async () => {
    try {
      console.log('🔍 Buscando cupons com filtros:', filters);
      setLoading(true);
      
      const data = await couponService.getAllCoupons({
        ...filters,
        page: page + 1,
        limit: rowsPerPage
      });
      
      console.log('📊 Dados recebidos da API:', data);
      console.log('📊 Tipo dos dados:', typeof data);
      console.log('📊 É array?', Array.isArray(data));
      console.log('📊 Chaves do objeto:', Object.keys(data));
      
      // Tentar forçar verificação da estrutura
      if (Array.isArray(data)) {
        console.warn('⚠️ PROBLEMA: Recebeu array direto, esperava objeto { coupons, total }');
        // Adaptar para estrutura esperada temporariamente
        setCoupons(data);
        setTotal(data.length);
      } else if (data && typeof data === 'object') {
        console.log('📝 Cupons encontrados:', data.coupons);
        console.log('📈 Total de cupons:', data.total);
        
        if (data.coupons) {
          setCoupons(data.coupons);
          setTotal(data.total || 0);
        } else {
          console.warn('⚠️ Estrutura de dados inesperada:', data);
          setCoupons([]);
          setTotal(0);
        }
      } else {
        console.error('❌ Dados inválidos recebidos:', data);
        setCoupons([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar cupons:', error);
      setCoupons([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  const fetchCampaigns = useCallback(async () => {
    try {
      console.log('🏪 Buscando campanhas...');
      const data = await campaignService.getCampaignsByAssociation();
      console.log('📋 Campanhas encontradas:', data);
      setCampaigns(data);
    } catch (error) {
      console.error('❌ Erro ao carregar campanhas:', error);
      setCampaigns([]);
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      console.log('🏬 Buscando lojas...');
      const data = await storeService.getStoresByAssociation();
      console.log('🏪 Lojas encontradas:', data);
      setStores(data.stores);
    } catch (error) {
      console.error('❌ Erro ao carregar lojas:', error);
      setStores([]);
    }
  }, []);

  useEffect(() => {
    console.log('🚀 useEffect executado - carregando dados...');
    fetchCoupons();
    fetchCampaigns();
    fetchStores();
  }, [fetchCoupons, fetchCampaigns, fetchStores]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(0);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este cupom?')) {
      try {
        await couponService.deleteCoupon(id);
        await fetchCoupons();
      } catch (error) {
        console.error('Erro ao excluir cupom:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'USED':
        return 'info';
      case 'INVALID':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Ativo';
      case 'USED':
        return 'Usado';
      case 'INVALID':
        return 'Inválido';
      default:
        return status;
    }
  };

  return (
    <>
      <AppHeader 
        title="Gerenciar Cupons" 
        subtitle="Visualize e gerencie todos os cupons do sistema"
        icon={<ConfirmationNumber />}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Cupons
            </Typography>
            {!loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {Object.values(filters).some(filter => filter !== '') 
                  ? `${total} cupons encontrados com os filtros aplicados`
                  : `${total} cupons cadastrados no total`
                }
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/coupons/new')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Novo Cupom
          </Button>
        </Box>

        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              color: 'primary.main', 
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            🔍 Filtros de Busca
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Use os filtros abaixo para encontrar cupons específicos. Todos os filtros são independentes e podem ser usados em qualquer combinação.
          </Typography>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Número do Cupom"
              name="number"
              value={filters.number}
              onChange={handleFilterChange}
              size="small"
              placeholder="Ex: 123456"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Campanha</InputLabel>
              <Select
                name="campaignId"
                value={filters.campaignId}
                onChange={handleFilterChange}
                label="Campanha"
              >
                <MenuItem value="">
                  <em>Todas as campanhas</em>
                </MenuItem>
                {campaigns?.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Loja</InputLabel>
              <Select
                name="storeId"
                value={filters.storeId}
                onChange={handleFilterChange}
                label="Loja"
              >
                <MenuItem value="">
                  <em>Todas as lojas</em>
                </MenuItem>
                {stores?.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Nome do Vendedor"
              name="sellerName"
              value={filters.sellerName}
              onChange={handleFilterChange}
              size="small"
              placeholder="Ex: João, Maria"
            />
            <TextField
              fullWidth
              label="Nome do Usuário"
              name="userName"
              value={filters.userName}
              onChange={handleFilterChange}
              size="small"
              placeholder="Ex: Cliente, Comprador"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="">
                  <em>Todos os status</em>
                </MenuItem>
                <MenuItem value="ACTIVE">✅ Ativo</MenuItem>
                <MenuItem value="USED">📝 Usado</MenuItem>
                <MenuItem value="INVALID">❌ Inválido</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={() => {
                console.log('🧹 Limpando filtros...');
                setFilters({
                  number: '',
                  campaignId: '',
                  storeId: '',
                  sellerName: '',
                  userName: '',
                  status: ''
                });
                setPage(0);
              }}
              size="small"
              sx={{ minWidth: '100px', height: 'fit-content' }}
            >
              🧹 Limpar
            </Button>
          </Stack>

          {/* Indicador de filtros ativos */}
          {Object.values(filters).some(filter => filter !== '') && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
                🎯 Filtros ativos: {Object.entries(filters).filter(([_, value]) => value !== '').map(([key, value]) => {
                  const labels: {[key: string]: string} = {
                    number: 'Número',
                    campaignId: campaigns.find(c => c.id === value)?.name || 'Campanha',
                    storeId: stores.find(s => s.id === value)?.name || 'Loja',
                    sellerName: `Vendedor: ${value}`,
                    userName: `Usuário: ${value}`,
                    status: `Status: ${getStatusLabel(value)}`
                  };
                  return labels[key] || value;
                }).join(' • ')}
              </Typography>
            </Box>
          )}
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell>Campanha</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell>Loja</TableCell>
                <TableCell>Vendedor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data Criação</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary">
                        {Object.values(filters).some(filter => filter !== '')
                          ? 'Buscando cupons com os filtros aplicados...'
                          : 'Carregando cupons...'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : coupons?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
                        🔍 Nenhum cupom encontrado
                      </Typography>
                      {Object.values(filters).some(filter => filter !== '') ? (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Não foram encontrados cupons que correspondam aos filtros aplicados.
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setFilters({
                                number: '',
                                campaignId: '',
                                storeId: '',
                                sellerName: '',
                                userName: '',
                                status: ''
                              });
                              setPage(0);
                            }}
                          >
                            🧹 Limpar filtros e ver todos os cupons
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Ainda não há cupons cadastrados no sistema.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                coupons?.map((coupon) => (
                  <TableRow key={coupon.id}>
                    <TableCell>
                      <Typography variant="body2">
                        {coupon.number}
                      </Typography>
                    </TableCell>
                    <TableCell>{coupon.campaign?.name || 'N/A'}</TableCell>
                    <TableCell>{coupon.user?.name || 'N/A'}</TableCell>
                    <TableCell>{coupon.store?.name || 'N/A'}</TableCell>
                    <TableCell>{coupon.sellerName || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(coupon.status)}
                        color={getStatusColor(coupon.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(coupon.createdAt).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(coupon.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Itens por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TableContainer>
      </Container>
    </>
  );
}; 
export default CouponList;